import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VerifiedIcon from '@mui/icons-material/Verified';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { aiSafety } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import { useIsMobile } from '@/hooks/useIsMobile';

type AIModel = {
  id: number;
  name: string;
  provider: string;
  version: string;
  model_type: 'llm' | 'classifier' | 'asr' | 'embeddings' | 'vision' | 'other';
  intended_use: string;
  forbidden_use: string;
  training_data_summary: string;
  evaluation_summary: string;
  privacy_posture: string;
  is_active: boolean;
  approved_for_medical_use: boolean;
  approved_by: number | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
};

const MODEL_TYPES = [
  { code: 'llm',        label: 'LLM' },
  { code: 'classifier', label: 'Classifier' },
  { code: 'asr',        label: 'Speech-to-text (ASR)' },
  { code: 'embeddings', label: 'Embeddings' },
  { code: 'vision',     label: 'Vision' },
  { code: 'other',      label: 'Other' },
];

export function AISafetyModelsPage() {
  const q = useApiQuery({
    queryKey: ['ai-safety', 'models'],
    queryFn:  aiSafety.models.list as () => Promise<AIModel[]>,
  });

  const [editing, setEditing] = useState<AIModel | null>(null);
  const [creating, setCreating] = useState(false);
  const [approving, setApproving] = useState<AIModel | null>(null);

  const qc = useQueryClient();
  const toast = useToast();

  const approveMut = useApiMutation({
    mutationFn: (vars: { id: number; reason?: string }) =>
      aiSafety.models.approve(vars.id, vars.reason),
    onSuccess: () => {
      toast.success('Model approved.');
      qc.invalidateQueries({ queryKey: ['ai-safety', 'models'] });
      setApproving(null);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="AI model registry"
        subtitle="Every model used by any capability — its provider, version, intended use, and approval status."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            Register model
          </Button>
        }
      />

      <DataState query={q} empty={{ title: 'No models registered yet' }}>
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((m) => (
              <Row key={m.id} model={m}
                   onEdit={() => setEditing(m)}
                   onApprove={() => setApproving(m)} />
            ))}
          </Stack>
        )}
      </DataState>

      {(creating || editing) && (
        <ModelDialog model={editing}
                     onClose={() => { setEditing(null); setCreating(false); }} />
      )}

      <ConfirmDialog
        open={approving !== null}
        title={`Approve "${approving?.name}" for medical use?`}
        description="Approval requires a documented evaluation and recorded intended use. Approval is recorded with your identity and timestamp."
        confirmLabel="Approve"
        onCancel={() => setApproving(null)}
        onConfirm={(reason) =>
          approving
            ? approveMut.mutateAsync({ id: approving.id, reason: reason ?? '' })
            : Promise.resolve()
        }
      />
    </>
  );
}

function Row({
  model, onEdit, onApprove,
}: { model: AIModel; onEdit: () => void; onApprove: () => void }) {
  return (
    <Card sx={{ borderColor: model.approved_for_medical_use ? 'success.main' : 'divider' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle',
            color: model.approved_for_medical_use ? 'success.main' : 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <SmartToyIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {model.name}
              </Typography>
              <Chip size="small" label={`v${model.version}`}
                    variant="outlined" className="tabular" />
              <Chip size="small" label={model.model_type}
                    variant="outlined" sx={{ textTransform: 'capitalize' }} />
              {model.approved_for_medical_use ? (
                <Chip size="small" color="success" variant="outlined"
                      icon={<VerifiedIcon />} label="approved" />
              ) : (
                <Chip size="small" color="warning" variant="outlined" label="pending approval" />
              )}
              {!model.is_active && (
                <Chip size="small" label="inactive" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {model.provider}
              {model.approved_at && (
                <> · approved <span className="tabular">{formatDateTime(model.approved_at)}</span></>
              )}
            </Typography>
            {model.intended_use && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }} noWrap>
                {model.intended_use}
              </Typography>
            )}
          </Box>
          {!model.approved_for_medical_use && (
            <Button size="small" variant="contained" color="success"
                    startIcon={<VerifiedIcon />}
                    onClick={onApprove}>
              Approve
            </Button>
          )}
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ModelDialog({
  model, onClose,
}: { model: AIModel | null; onClose: () => void }) {
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = model !== null;

  const [name, setName] = useState(model?.name ?? '');
  const [provider, setProvider] = useState(model?.provider ?? '');
  const [version, setVersion] = useState(model?.version ?? '');
  const [modelType, setModelType] = useState(model?.model_type ?? 'llm');
  const [intendedUse, setIntendedUse] = useState(model?.intended_use ?? '');
  const [forbiddenUse, setForbiddenUse] = useState(model?.forbidden_use ?? '');
  const [trainingData, setTrainingData] = useState(model?.training_data_summary ?? '');
  const [evaluation, setEvaluation] = useState(model?.evaluation_summary ?? '');
  const [privacy, setPrivacy] = useState(model?.privacy_posture ?? '');
  const [isActive, setIsActive] = useState(model?.is_active ?? true);

  useEffect(() => {
    setName(model?.name ?? '');
    setProvider(model?.provider ?? '');
    setVersion(model?.version ?? '');
    setModelType(model?.model_type ?? 'llm');
    setIntendedUse(model?.intended_use ?? '');
    setForbiddenUse(model?.forbidden_use ?? '');
    setTrainingData(model?.training_data_summary ?? '');
    setEvaluation(model?.evaluation_summary ?? '');
    setPrivacy(model?.privacy_posture ?? '');
    setIsActive(model?.is_active ?? true);
  }, [model?.id]);

  const saveMut = useApiMutation({
    mutationFn: () => {
      const payload = {
        name, provider, version,
        model_type: modelType,
        intended_use: intendedUse,
        forbidden_use: forbiddenUse,
        training_data_summary: trainingData,
        evaluation_summary: evaluation,
        privacy_posture: privacy,
        is_active: isActive,
      };
      return isEdit
        ? aiSafety.models.update(model!.id, payload)
        : aiSafety.models.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Model updated.' : 'Model registered.');
      qc.invalidateQueries({ queryKey: ['ai-safety', 'models'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = name.trim() && provider.trim() && version.trim() && intendedUse.trim();

  return (
    <Dialog fullScreen={isMobile} open onClose={() => !saveMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit model' : 'Register model'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Name"
                       value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="GPT-4o, Claude 3.7 Sonnet, …" />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth required label="Provider"
                       value={provider} onChange={(e) => setProvider(e.target.value)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth required label="Version"
                       value={version} onChange={(e) => setVersion(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Model type"
                       value={modelType}
                       onChange={(e) => setModelType(e.target.value as AIModel['model_type'])}>
              {MODEL_TYPES.map((t) => (
                <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={isActive}
                               onChange={(_, v) => setIsActive(v)} />}
              label="Active (eligible for use by capabilities)" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required multiline minRows={2}
                       label="Intended use"
                       value={intendedUse}
                       onChange={(e) => setIntendedUse(e.target.value)}
                       helperText="What is this model meant to do?" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2}
                       label="Forbidden use"
                       value={forbiddenUse}
                       onChange={(e) => setForbiddenUse(e.target.value)}
                       helperText="What is it explicitly NOT to be used for?" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline minRows={3}
                       label="Training data summary"
                       value={trainingData}
                       onChange={(e) => setTrainingData(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline minRows={3}
                       label="Evaluation summary"
                       value={evaluation}
                       onChange={(e) => setEvaluation(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2}
                       label="Privacy posture"
                       value={privacy}
                       onChange={(e) => setPrivacy(e.target.value)}
                       helperText="Data residency, retention, training opt-out, etc." />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {isEdit ? 'Save' : 'Register'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
